import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Etobicoke = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

  const cheapestLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/'

  const getAbout = () => {
    return (
      <>
        <Trans i18nKey="seo.city.etobicoke:About Etobicoke Paragraph 1">
          <Paragraph>
            Etobicoke is a modern suburban district within the{' '}
            <LinkText href={cheapestLink}>Greater Toronto Area (GTA)</LinkText>{' '}
            of <LinkText href="/rental-listings/ontario">Ontario</LinkText> that
            has plenty to love for renters. The sprawling waterfront community
            is surrounded by green space including lush parks and extensive
            waterfront trails along the area’s rivers and overlooking the shores
            of Lake Ontario. Convenience certainly isn’t an issue here either.
            Despite its tranquil setting, Etobicoke has plenty of major shopping
            centres, dining options, and is just a 25-minute drive to{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/neighbourhood-guide-downtown-toronto/">
              Downtown Toronto
            </LinkText>
            .
          </Paragraph>
        </Trans>
        <Trans i18nKey="seo.city.etobicoke:About Etobicoke Paragraph 2">
          <Paragraph>
            Etobicoke’s many distinct neighbourhoods have a decidedly
            cosmopolitan feel despite their suburban setting, and renters can
            find a variety of housing options within the district. Recent years
            have seen plenty of high-rise condos, while the area continues to
            offer an abundance of row homes as well as detached houses in its
            lakeside neighbourhoods.
          </Paragraph>
        </Trans>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$2,000 - $2,800',
          bed2: '$2,500 - $3,500',
          bed3: '$3,000 - $4,500'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E5%A4%9A%E4%BC%A6%E5%A4%9A/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.etobicoke:Apartment Hunting in Etobicoke Paragraph 1">
            Etobicoke is a desirable surburb of{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>{' '}
            with rent prices that are consistently above the region’s average.
            As such, it will be hard to find{' '}
            <LinkText href={cheapestLink}>cheap rentals in Etobicoke</LinkText>{' '}
            - especially if you’re trying to find{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>
            . However, to start your search, you can refer to this guide on the{' '}
            <LinkText href={cheapestLink}>
              cheapest neighbourhoods for rent in Greater Toronto Area (GTA)
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Apartments for rent LOCATION 2 Paragraph">
              Apartments are the most common form of housing you’ll find in
              densely-populated {{ cityName }}. Renters should expect to see
              higher price tags for{' '}
              <LinkText href={links.apartment}>
                apartments for rent in {{ cityName }}
              </LinkText>
              , especially compared to other Canadian provinces. This doesn’t
              mean that there aren’t bargains to be found though, but the cities
              proximity to nature and excellent weather makes it all worthwhile.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Houses for rent CITY Paragraph 2">
              There are plenty of{' '}
              <LinkText href={links.house}>
                houses for rent in {{ cityName }}
              </LinkText>{' '}
              for those with a higher budget, or who have multiple roommates. In
              more residential areas of the city, both partial houses and entire
              houses for rent are common – as well as both new builds and older
              homes from the city’s earlier days. {{ cityName }} is also known
              for an abundance of laneway homes and townhomes within the city,
              making it easy to find a place to rent that suits your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.etobicoke:Basements for rent Etobicoke Paragraph">
              If you’re searching for affordable rental housing in Etobicoke,
              consider the option of basements for rent. With rising house
              prices, homeowners often rent out their basements to help cover
              their mortgage expenses. This means you can easily find reasonably
              priced basements for rent in Etobicoke. It’s a practical
              alternative that offers affordability in various areas of
              Etobicoke.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.etobicoke:Condos for rent Etobicoke Paragraph">
              Etobicoke is an urban location, and{' '}
              <LinkText href={links.apartment}>
                condos for rent in Etobicoke
              </LinkText>{' '}
              are a common sight. These types of rental accommodation are
              perfect for those who like to be in the centre of the action, and
              don’t need as much space. If you are looking to rent in the city
              centre though, be prepared for higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Short term rentals CITY Paragraph">
              {{ cityName }} has plenty of work opportunities and world-class
              academic institutions, making it a popular place for those renting
              on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>
                Short-term rentals in {{ cityName }}
              </LinkText>{' '}
              are exceedingly popular, particularly during the summer months and
              at the beginning of school semesters. The added flexibility is an
              appealing prospect for many renters, and a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION Paragraph">
              {{ cityName }} isn’t known for inexpensive rent, but if you’re
              careful in your search, it shouldn’t be too hard to find something
              within your budget. If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.etobicoke:Living in Etobicoke Paragraph">
          Etobicoke, Ontario offers a suburban lifestyle with convenient access
          to <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>.
          Known as one of the{' '}
          <LinkText href={neighborhoodsLink}>
            best places to rent in the GTA
          </LinkText>
          , it features diverse housing options, parks, and waterfront trails.
          Residents enjoy a family-friendly atmosphere, shopping centers, and
          recreational facilities. Etobicoke provides a balanced blend of
          suburban tranquility and urban convenience, making it a desirable
          place to live near Toronto.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.etobicoke:Popular Neighbourhoods Etobicoke Paragraph">
          Like any city, Etobicoke is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Humber Bay, Mimico, Kingsway South, Long Branch, Rexdale,
          Markland Wood, and The Queensway. For more info about Etobicoke, you
          can consult this{' '}
          <LinkText href={neighborhoodsLink}>
            guide on Toronto neighbourhoods
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.etobicoke:Local Landmarks Etobicoke Paragraph">
          Etobicoke is famous for its lakeside parks and trails, most notably
          Humber Bay Park East, which consists of 19 acres of sprawling
          parkland. Filled to the brim with history, Etobicoke residents can
          explore the famous Montgomery Inn which has stood in the area since
          1832 and currently hosts a museum, tea room, and a weekly farmers’
          market. The Islington Mosaic is another interesting historical sight
          for residents to check out, depicting Etobicoke life between
          1910-1960.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Etobicoke
